// ─── generators/facturen.js ──────────────────────────────────────────────────
// Rapportgenerator: genFacturen.
// Wijzig hier bij aanpassingen aan dit specifieke rapporttype.

import { MAANDEN, MAANDEN_K, fmtE, fmtD, fmtKg, fmtK, diff, statusLabel, statusBadge, isOnderweg } from '../helpers.js';
import { header, kpiBlok, pageCss, sectTitel, sectTitelKlein, footer, wrapMetHerhalendeHeader } from '../layout.js';
import { balkGrafiek, balkGrafiek3D, lijnGrafiek, taartGrafiek, dubbeleGrafiek, hBalk, renderRanking, renderGrafiek } from '../charts.js';
import { verwerkData } from '../data.js';

function genFacturen(facturen, bedrijfsnaam, cfg, logo) {
  let lijst = [...facturen];
  if(cfg.filter==='open')    lijst=lijst.filter(a=>(a.Status||a.FactuurStatus||'')!=='Betaald');
  if(cfg.filter==='betaald') lijst=lijst.filter(a=>(a.Status||a.FactuurStatus||'')==='Betaald');
  // Gebruik factuurVan/factuurTot (aparte velden van de aanvragen-periode)
  const fVan = cfg.factuurVan || '';
  const fTot = cfg.factuurTot || '';
  // FactuurDatum kan een string ('2026-01-15') of Date-object zijn (ExcelJS)
  const toIso = v => {
    if(!v) return '';
    if(v instanceof Date) return v.toISOString().slice(0,10);
    return String(v).slice(0,10);
  };
  if(fVan) lijst=lijst.filter(a=>toIso(a.FactuurDatum)>=fVan);
  if(fTot) lijst=lijst.filter(a=>toIso(a.FactuurDatum)<=fTot);
  if(cfg.sorteer==='datum-asc')    lijst.sort((a,b)=>(a.FactuurDatum||'').localeCompare(b.FactuurDatum||''));
  else if(cfg.sorteer==='bedrag-desc') lijst.sort((a,b)=>(parseFloat(b.TotaalPrijs)||0)-(parseFloat(a.TotaalPrijs)||0));
  else lijst.sort((a,b)=>(b.FactuurDatum||'').localeCompare(a.FactuurDatum||''));

  const fmtB = n => `€\u202f${Number(n||0).toLocaleString('nl-NL',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const totaal    = lijst.reduce((s,a)=>s+(parseFloat(a.TotaalPrijs)||0),0);
  // KPI's altijd op volledige lijst (ongeacht filter)
  const totOpen   = facturen.filter(a=>(a.Status||a.FactuurStatus||'')!=='Betaald').reduce((s,a)=>s+(parseFloat(a.TotaalPrijs)||0),0);
  const totBetaald= facturen.filter(a=>(a.Status||a.FactuurStatus||'')==='Betaald').reduce((s,a)=>s+(parseFloat(a.TotaalPrijs)||0),0);

  const _css = '<style>' + pageCss(cfg.orientatie) + '</style>';
  const _hdr = header('Factuuroverzicht', `${lijst.length} facturen`, bedrijfsnaam, logo);
  let body = '';

  body += kpiBlok([
    {lbl:'Openstaand',  val:fmtB(totOpen),    sub:`${facturen.filter(a=>(a.Status||a.FactuurStatus||'')!=='Betaald').length} facturen`, kleur:'#ef4444'},
    {lbl:'Betaald',     val:fmtB(totBetaald), sub:`${facturen.filter(a=>(a.Status||a.FactuurStatus||'')==='Betaald').length} facturen`, kleur:'#10b981'},
    {lbl:'Totaal',      val:fmtB(totOpen+totBetaald), sub:`${facturen.length} facturen totaal`, kleur:'#d4a848'},
    {lbl:'In selectie', val:fmtB(totaal), sub:`${lijst.length} facturen`, kleur:'#1a364c'},
  ]);

  body += `<table style="width:100%;border-collapse:collapse;font-size:10px;">
    <thead><tr style="background:#f7f6f2;">
      ${['Factuur nr','Aanvraag','Datum','Bestemming','Status','Bedrag'].map(h=>`<th style="padding:6px 8px;text-align:${h==='Bedrag'?'right':'left'};font-size:9px;font-weight:700;color:#1a364c;text-transform:uppercase;letter-spacing:.05em;border-bottom:1px solid #e8edf3;">${h}</th>`).join('')}
    </tr></thead>
    <tbody>${lijst.map((a,i)=>{
      const betaald=(a.Status||a.FactuurStatus||'')==='Betaald';
      // Haal losplaats uit LosAdres veld (bijv "Via Roma 45, 20121 Milaan (Italië)")
      const losInfo = a.LosAdres ? a.LosAdres.split(',').pop().trim() : (a.KlantPlaats||'—');
      return `<tr style="border-bottom:1px solid #f5f5f5;background:${i%2===0?'white':'#fafafa'};">
        <td style="padding:5px 8px;font-weight:700;color:#1a364c;">${a.FactuurNr||'—'}</td>
        <td style="padding:5px 8px;">${a.AanvraagID||'—'}</td>
        <td style="padding:5px 8px;">${fmtD(toIso(a.FactuurDatum))}</td>
        <td style="padding:5px 8px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:120px;">${losInfo}</td>
        <td style="padding:5px 8px;"><span style="display:inline-block;padding:2px 8px;border-radius:20px;font-size:10px;font-weight:600;background:${betaald?'#d1fae5':'#fef3c7'};color:${betaald?'#065f46':'#92400e'};">${betaald?'Betaald':'Open'}</span></td>
        <td style="padding:5px 8px;text-align:right;font-weight:600;">${fmtB(a.TotaalPrijs)}</td>
      </tr>`;}).join('')}
    </tbody>
    <tfoot><tr style="border-top:2px solid #1a364c;background:#f7f6f2;">
      <td colspan="5" style="padding:7px 8px;font-weight:700;color:#1a364c;">Totaal selectie (${lijst.length} facturen)</td>
      <td style="padding:7px 8px;text-align:right;font-weight:700;color:#1a364c;">${fmtB(totaal)}</td>
    </tr></tfoot>
  </table>`;

  return wrapMetHerhalendeHeader(_css, _hdr, body);
}

export { genFacturen };
